import PropTypes from 'prop-types';
import { describePages } from '../../utils/ocrSearchFallback';

/**
 * Shown over the viewer when its own search found nothing but the
 * document's OCR text holds the words. Tells the reader which pages, and
 * walks them there — the viewer cannot draw a highlight on a scanned page
 * with no text layer, so the page is what we can give.
 */
const OcrSearchFallbackBanner = ({ query, pages, index, onGoTo, onStep, onDismiss }) => {
  if (!pages || !pages.length) return null;
  const current = pages[index] ?? pages[0];
  const many = pages.length > 1;
  return (
    <div
      role="status"
      data-testid="ocr-search-fallback"
      style={{
        position: 'absolute',
        top: 84,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 20,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        maxWidth: 'calc(100% - 48px)',
        padding: '8px 14px',
        borderRadius: 8,
        background: '#1f2937',
        color: '#f9fafb',
        boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
        fontSize: 13,
        lineHeight: 1.35,
      }}
    >
      <span>
        These pages are scanned, so the viewer’s search can’t see the words. Our reading of the
        document found <strong>“{query}”</strong>: {describePages(pages)}.
      </span>
      {many && (
        <button type="button" onClick={() => onStep(-1)} style={stepStyle} aria-label="Previous page with a match">
          ‹
        </button>
      )}
      <button type="button" onClick={() => onGoTo(current)} style={goStyle}>
        {many ? `Go to page ${current} (${index + 1} of ${pages.length})` : `Go to page ${current}`}
      </button>
      {many && (
        <button type="button" onClick={() => onStep(1)} style={stepStyle} aria-label="Next page with a match">
          ›
        </button>
      )}
      <button type="button" onClick={onDismiss} style={closeStyle} aria-label="Dismiss">
        ×
      </button>
    </div>
  );
};

const buttonBase = {
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 13,
  lineHeight: 1,
  whiteSpace: 'nowrap',
};
const goStyle = { ...buttonBase, background: '#3b82f6', color: '#fff', padding: '7px 12px', fontWeight: 600 };
const stepStyle = { ...buttonBase, background: '#374151', color: '#fff', padding: '6px 10px', fontSize: 16 };
const closeStyle = { ...buttonBase, background: 'transparent', color: '#d1d5db', padding: '4px 6px', fontSize: 18 };

OcrSearchFallbackBanner.propTypes = {
  query: PropTypes.string.isRequired,
  pages: PropTypes.arrayOf(PropTypes.number).isRequired,
  index: PropTypes.number.isRequired,
  onGoTo: PropTypes.func.isRequired,
  onStep: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

export default OcrSearchFallbackBanner;
